import { useEffect, useMemo, useState } from "react";
import "./App.css";
import Home from "./apps/Home";
import { endpoints } from "./api/endpoints";
import { hubConfig } from "./apps/routes";

const AUTH_STATUS = {
  CHECKING: "checking",
  AUTHORIZED: "authorized",
  UNAUTHORIZED: "unauthorized",
};

function buildAuthCheckUrl(baseUrl, checkPath) {
  if (!baseUrl) return "";

  try {
    return new URL(checkPath, baseUrl).toString();
  } catch {
    const normalizedBase = baseUrl.endsWith("/")
      ? baseUrl.slice(0, -1)
      : baseUrl;
    const normalizedPath = checkPath.startsWith("/")
      ? checkPath
      : `/${checkPath}`;
    return `${normalizedBase}${normalizedPath}`;
  }
}

function extractUser(payload) {
  const source = payload?.user || payload || {};

  const username =
    source?.username ||
    source?.user_name ||
    source?.name ||
    source?.full_name ||
    source?.display_name ||
    "User";

  const email = source?.email || source?.user_email || "";

  return { username, email };
}

function App() {
  const [authStatus, setAuthStatus] = useState(AUTH_STATUS.CHECKING);
  const [user, setUser] = useState({ username: "User", email: "" });
  const [perms, setPerms] = useState({});

  const apiBaseUrl = (endpoints.MITCO_API_URL || "").trim();
  const authCheckUrl = useMemo(
    () => buildAuthCheckUrl(apiBaseUrl, hubConfig.authCheckPath),
    [apiBaseUrl],
  );
  const authLogoutUrl = useMemo(
    () =>
      buildAuthCheckUrl(
        apiBaseUrl,
        hubConfig.authLogoutPath || "/users/logout/",
      ),
    [apiBaseUrl],
  );

  useEffect(() => {
    document.title = hubConfig.appName || "COMPANY HUB";
  }, []);

  const handleLogin = () => {
    if (apiBaseUrl) {
      window.location.assign(apiBaseUrl);
    }
  };

  const handleLogout = async () => {
    try {
      if (authLogoutUrl) {
        await fetch(authLogoutUrl, {
          method: "POST",
          credentials: "include",
          headers: {
            Accept: "application/json",
          },
        });
      }
    } catch {}

    if (apiBaseUrl) {
      window.location.assign(apiBaseUrl);
    }
  };

  useEffect(() => {
    let isActive = true;

    const redirectToApi = () => {
      if (apiBaseUrl) {
        window.location.assign(apiBaseUrl);
      }
    };

    const checkAuth = async () => {
      if (!apiBaseUrl || !authCheckUrl) {
        if (isActive) setAuthStatus(AUTH_STATUS.UNAUTHORIZED);
        redirectToApi();
        return;
      }

      try {
        const response = await fetch(authCheckUrl, {
          method: "GET",
          credentials: "include",
          headers: {
            Accept: "application/json",
          },
        });

        if (!response.ok) {
          throw new Error("Auth check failed");
        }

        let payload = {};
        try {
          payload = await response.json();
        } catch {
          payload = {};
        }

        const explicitAuthFailure =
          payload?.authenticated === false ||
          payload?.is_authenticated === false ||
          payload?.isAuthenticated === false ||
          payload?.success === false;

        if (explicitAuthFailure) {
          throw new Error("User not authenticated");
        }

        if (isActive) {
          setUser(extractUser(payload));

          // Fetch workspace permissions (non-fatal)
          try {
            const permsUrl = buildAuthCheckUrl(
              apiBaseUrl,
              endpoints.WORKSPACE_PERMS,
            );
            const permsRes = await fetch(permsUrl, {
              method: "GET",
              credentials: "include",
              headers: { Accept: "application/json" },
            });
            if (permsRes.ok) {
              const nextPerms = await permsRes.json();
              setPerms(nextPerms);
            }
          } catch {
            // Non-fatal — render with no perms filter rather than block
          }

          setAuthStatus(AUTH_STATUS.AUTHORIZED);
        }
      } catch {
        if (isActive) setAuthStatus(AUTH_STATUS.UNAUTHORIZED);
        setTimeout(redirectToApi, 800);
      }
    };

    checkAuth();

    return () => {
      isActive = false;
    };
  }, [apiBaseUrl, authCheckUrl]);

  if (authStatus === AUTH_STATUS.CHECKING) {
    return (
      <div className="hub-status-screen">
        <div className="hub-status-card">
          <h1>Checking authentication…</h1>
          <p>Verifying your session before loading the workspace.</p>
        </div>
      </div>
    );
  }

  if (authStatus === AUTH_STATUS.UNAUTHORIZED) {
    return (
      <div className="hub-status-screen">
        <div className="hub-status-card">
          <h1>Session not available</h1>
          <p>Redirecting to authentication.</p>
          <button
            type="button"
            className="hub-login-button"
            onClick={handleLogin}
          >
            Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <Home
        user={user}
        perms={perms}
        isAuthenticated={authStatus === AUTH_STATUS.AUTHORIZED}
        onLogin={handleLogin}
        onLogout={handleLogout}
      />
    </div>
  );
}

export default App;
